
import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { listStaff } from "../api/staffs";
import {
  postCategoryRevenueByCategory,
  postCategoryMixOverTime,
  postCategoryStaffingInsights,
  postCategoryGrowth,
  postCategoryConcentration,
  postCategorySeasonality,
} from "../api/reports";
import { getSalonTimezone } from "../utils/salonTimezone";
import InfoTooltip from "../components/common/InfoTooltip";
import SortableTh from "../components/common/SortableTh";
import ReportPageHeader from "../components/reports/ReportPageHeader";
import ReportHelpCard from "../components/reports/ReportHelpCard";
import useTableSort from "../hooks/useTableSort";
import useHelpGuideNavigation from "../hooks/useHelpGuideNavigation";
import { CATEGORY_ANALYTICS_TOOLTIPS } from "../constants/reportTooltips";
import { REPORT_HELP } from "../constants/reportHelpContent";
import "./ReportsSectionCompact.css";

const formatDateISO = (date, timeZone) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  if (timeZone) {
    try {
      const formatter = new Intl.DateTimeFormat("en-CA", {
        timeZone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });
      return formatter.format(date);
    } catch (err) {
      // fall back to local time
    }
  }
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const addDays = (base, days) => {
  const next = new Date(base.getTime());
  next.setDate(next.getDate() + days);
  return next;
};

const formatMoney = (value) => {
  const amount = Number(value ?? 0);
  if (!Number.isFinite(amount)) return "$0.00";
  return `$${amount.toFixed(2)}`;
};

const formatNumber = (value, digits = 0) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return "--";
  return num.toFixed(digits);
};

const formatPercent = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return "--";
  return `${num.toFixed(1)}%`;
};

const ALERT_CLASS =
  "rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700";

const FIELD_CLASS =
  "h-10 px-3 text-sm md:text-base rounded-xl border border-slate-300 shadow-sm focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10 disabled:bg-slate-50";

const LABEL_TEXT = "text-xs font-semibold uppercase tracking-wide text-slate-500";

const BTN_PRIMARY =
  "h-10 px-5 rounded-full bg-rose-500 text-white text-sm md:text-base font-semibold hover:bg-rose-600 disabled:opacity-60 disabled:cursor-not-allowed";

const BTN_OUTLINE =
  "h-10 px-5 rounded-full border border-slate-300 text-slate-700 text-sm md:text-base font-semibold hover:border-slate-400";

const TAB_BASE =
  "h-9 md:h-10 px-4 rounded-full text-sm font-semibold transition";

const TAB_ACTIVE = "bg-rose-500 text-white";

const TAB_INACTIVE = "border border-slate-300 text-slate-700 hover:border-slate-400";

const CAP_OPTIONS = [
  { value: "2", label: "2" },
  { value: "4", label: "4" },
  { value: "6", label: "6" },
  { value: "8", label: "8" },
  { value: "12", label: "12" },
  { value: "none", label: "No cap" },
];

const TIME_BUCKET_OPTIONS = [
  { value: "day", label: "Day" },
  { value: "week", label: "Week" },
  { value: "month", label: "Month" },
  { value: "quarter", label: "Quarter" },
  { value: "year", label: "Year" },
];

const TABS = [
  { key: "categoryTotals", label: "Category Totals" },
  { key: "categoryMix", label: "Category Mix" },
  { key: "categoryStaffing", label: "Category Staffing" },
  { key: "categoryInsights", label: "Category Insights" },
];
export default function CategoryAnalyticsReport() {
  const { currentSalon, loading: authLoading } = useAuth();
  const { jumpToSectionWithHighlight, getSectionClassName } = useHelpGuideNavigation();
  const helpGuide = REPORT_HELP.CATEGORY_ANALYTICS;
  const salonTimezone = useMemo(
    () => getSalonTimezone(currentSalon),
    [currentSalon]
  );

  const today = formatDateISO(new Date(), salonTimezone);
  const defaultStart = formatDateISO(addDays(new Date(), -6), salonTimezone);

  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(today);
  const [employeeId, setEmployeeId] = useState("");
  const [includeRefunds, setIncludeRefunds] = useState(false);
  const [includeDiscounts, setIncludeDiscounts] = useState(true);
  const [durationMode, setDurationMode] = useState("ALLOCATED");
  const [outlierCap, setOutlierCap] = useState("6");
  const [timeBucket, setTimeBucket] = useState("month");
  const [mixMetric, setMixMetric] = useState("net_sales");
  const [activeTab, setActiveTab] = useState("categoryTotals");

  const [staffOptions, setStaffOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [cache, setCache] = useState({});

  const togglePillClass = (active) =>
    `${TAB_BASE} ${active ? TAB_ACTIVE : TAB_INACTIVE}`;

  const resetFilters = () => {
    setStartDate(defaultStart);
    setEndDate(today);
    setEmployeeId("");
    setIncludeRefunds(false);
    setIncludeDiscounts(true);
    setDurationMode("ALLOCATED");
    setOutlierCap("6");
    setTimeBucket("month");
  };

  const loadStaff = useCallback(async () => {
    try {
      const staff = await listStaff();
      const rows = Array.isArray(staff)
        ? staff
        : Array.isArray(staff?.rows)
        ? staff.rows
        : [];
      setStaffOptions(rows);
    } catch (err) {
      setStaffOptions([]);
    }
  }, []);

  useEffect(() => {
    if (!authLoading) {
      loadStaff();
    }
  }, [authLoading, loadStaff]);

  const basePayload = useMemo(() => {
    const capValue = outlierCap === "none" ? null : Number(outlierCap);
    return {
      start_date: startDate,
      end_date: endDate,
      employee_id: employeeId ? Number(employeeId) : null,
      include_refunds: includeRefunds,
      include_discounts: includeDiscounts,
      duration_mode: durationMode,
      duration_outlier_cap_hours:
        capValue !== null && Number.isFinite(capValue) ? capValue : null,
    };
  }, [
    startDate,
    endDate,
    employeeId,
    includeRefunds,
    includeDiscounts,
    durationMode,
    outlierCap,
  ]);

  const cacheKey = useMemo(() => {
    const payload = { ...basePayload };
    if (activeTab === "categoryMix") {
      payload.time_bucket = timeBucket;
    }
    return `${activeTab}:${JSON.stringify(payload)}`;
  }, [activeTab, basePayload, timeBucket]);

  const activeData = cache[cacheKey];

  const runReport = useCallback(async () => {
    if (!startDate || !endDate) {
      setError("Select a valid start and end date.");
      return;
    }
    if (cache[cacheKey]) {
      return;
    }
    setLoading(true);
    setError("");
    try {
      let data;
      if (activeTab === "categoryTotals") {
        data = await postCategoryRevenueByCategory(basePayload);
      } else if (activeTab === "categoryMix") {
        data = await postCategoryMixOverTime({
          ...basePayload,
          time_bucket: timeBucket,
        });
      } else if (activeTab === "categoryStaffing") {
        data = await postCategoryStaffingInsights(basePayload);
      } else if (activeTab === "categoryInsights") {
        const [growth, concentration, seasonality] = await Promise.all([
          postCategoryGrowth(basePayload),
          postCategoryConcentration(basePayload),
          postCategorySeasonality(basePayload),
        ]);
        data = { growth, concentration, seasonality };
      }
      setCache((prev) => ({ ...prev, [cacheKey]: data }));
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to run report.");
    } finally {
      setLoading(false);
    }
  }, [
    activeTab,
    basePayload,
    cache,
    cacheKey,
    startDate,
    endDate,
    timeBucket,
  ]);

  const emptyState = (
    <div className="rounded-2xl border border-dashed border-slate-200 bg-white px-4 py-6 text-sm text-slate-500">
      Run the report to load this tab.
    </div>
  );

  const renderTopEmployees = (employees) => {
    if (!Array.isArray(employees) || employees.length === 0) {
      return "--";
    }
    return employees
      .map((emp) => emp.staff_name || `Staff ${emp.staff_id}`)
      .join(", ");
  };

  const renderTopCategoriesCompact = (categories, metric = "net") => {
    if (!Array.isArray(categories) || categories.length === 0) {
      return "--";
    }
    return categories
      .map((cat) => {
        const share =
          metric === "services"
            ? cat.share_services_sold
            : cat.share_net_sales;
        return `${cat.category_name} ${formatPercent(share)}`;
      })
      .join(" | ");
  };

  const renderFieldLabel = (label, tooltipKey) => (
    <div className="flex items-center gap-1.5">
      <label className={LABEL_TEXT}>{label}</label>
      {tooltipKey ? (
        <InfoTooltip text={CATEGORY_ANALYTICS_TOOLTIPS[tooltipKey]} label={label} />
      ) : null}
    </div>
  );

  const renderInlineLabel = (label, tooltipKey) => (
    <div className="flex items-center gap-1.5">
      <span className={LABEL_TEXT}>{label}</span>
      {tooltipKey ? (
        <InfoTooltip text={CATEGORY_ANALYTICS_TOOLTIPS[tooltipKey]} label={label} />
      ) : null}
    </div>
  );

  const categoryTotalsColumns = useMemo(
    () => ({
      category: { type: "text", getValue: (row) => row?.category_name || "" },
      services_sold: { type: "number", getValue: (row) => row?.services_sold },
      gross_sales: { type: "number", getValue: (row) => row?.gross_sales },
      discounts: { type: "number", getValue: (row) => row?.discounts },
      refunds: { type: "number", getValue: (row) => row?.refunds },
      net_sales: { type: "number", getValue: (row) => row?.net_sales },
      share_percent: { type: "percent", getValue: (row) => row?.share_percent },
      avg_net_per_service: {
        type: "number",
        getValue: (row) => row?.avg_net_per_service,
      },
    }),
    []
  );

  const categoryTotalsSort = useTableSort({
    rows: activeData?.rows || [],
    columns: categoryTotalsColumns,
    fallbackKey: "category",
  });

  const mixMetricKey = mixMetric === "services_sold" ? "services" : "net";

  const categoryMixColumns = useMemo(
    () => ({
      bucket: {
        type: "date",
        getValue: (row) => row?.bucket_start || row?.label,
      },
      total_net_sales: {
        type: "number",
        getValue: (row) => row?.total_net_sales,
      },
      top_categories: {
        type: "text",
        getValue: (row) =>
          renderTopCategoriesCompact(row?.categories || [], mixMetricKey),
      },
    }),
    [mixMetricKey]
  );

  const categoryMixSort = useTableSort({
    rows: activeData?.buckets || [],
    columns: categoryMixColumns,
    fallbackKey: "bucket",
  });

  const categoryStaffingColumns = useMemo(
    () => ({
      category: { type: "text", getValue: (row) => row?.category_name || "" },
      labor_hours: { type: "number", getValue: (row) => row?.labor_hours },
      services_sold: { type: "number", getValue: (row) => row?.services_sold },
      net_sales: { type: "number", getValue: (row) => row?.net_sales },
      revenue_per_labor_hour: {
        type: "number",
        getValue: (row) => row?.revenue_per_labor_hour,
      },
      top_employees: {
        type: "text",
        getValue: (row) => renderTopEmployees(row?.top_employees),
      },
    }),
    []
  );

  const categoryStaffingSort = useTableSort({
    rows: activeData?.rows || [],
    columns: categoryStaffingColumns,
    fallbackKey: "category",
  });

  const growthColumns = useMemo(
    () => ({
      category: { type: "text", getValue: (row) => row?.category_name || "" },
      net_sales: { type: "number", getValue: (row) => row?.net_sales },
      prev_net_sales: { type: "number", getValue: (row) => row?.prev_net_sales },
      delta_net_sales: { type: "number", getValue: (row) => row?.delta_net_sales },
      pct_change_net_sales: {
        type: "percent",
        getValue: (row) => row?.pct_change_net_sales,
      },
      services_sold: { type: "number", getValue: (row) => row?.services_sold },
    }),
    []
  );

  const growthSort = useTableSort({
    rows: activeData?.growth?.rows || [],
    columns: growthColumns,
    fallbackKey: "category",
  });

  const concentrationColumns = useMemo(
    () => ({
      category: { type: "text", getValue: (row) => row?.category_name || "" },
      net_sales: { type: "number", getValue: (row) => row?.net_sales },
      share_net_sales: { type: "percent", getValue: (row) => row?.share_net_sales },
      services_sold: { type: "number", getValue: (row) => row?.services_sold },
    }),
    []
  );

  const concentrationSort = useTableSort({
    rows: activeData?.concentration?.top_categories || [],
    columns: concentrationColumns,
    fallbackKey: "category",
  });

  const seasonalityColumns = useMemo(
    () => ({
      month: { type: "date", getValue: (row) => row?.bucket_start || row?.label },
      net_sales: { type: "number", getValue: (row) => row?.total_net_sales },
      services_sold: {
        type: "number",
        getValue: (row) => row?.total_services_sold,
      },
      top_category: {
        type: "text",
        getValue: (row) => row?.categories?.[0]?.category_name || "",
      },
      top_categories: {
        type: "text",
        getValue: (row) => renderTopCategoriesCompact(row?.categories || []),
      },
    }),
    []
  );

  const seasonalitySort = useTableSort({
    rows: activeData?.seasonality?.buckets || [],
    columns: seasonalityColumns,
    fallbackKey: "month",
  });
  return (
    <div className="reportSectionShell">
      <div className="reportSectionWide report-section-tight report-section-compact text-sm md:text-base">
        <div className="rounded-3xl border border-slate-200 bg-white shadow-2xl overflow-hidden">
          <ReportPageHeader title="Category Analytics" />

          <div className="px-6 py-6 space-y-6">
            <div
              id="category-analytics-overview"
              className={getSectionClassName("category-analytics-overview")}
            >
              <ReportHelpCard
                title={helpGuide.title}
                subtitle={helpGuide.subtitle}
                sections={helpGuide.sections}
                troubleshooting={helpGuide.troubleshooting}
                storageKey="report_help_category_analytics"
                onJumpToSection={jumpToSectionWithHighlight}
              />
            </div>
            {error && <div className={ALERT_CLASS}>{error}</div>}
            {loading && <div className={ALERT_CLASS}>Loading...</div>}

            <div
              id="category-analytics-filters"
              className={`rounded-2xl border border-slate-200 bg-white shadow-sm p-5 space-y-4 ${getSectionClassName(
                "category-analytics-filters"
              )}`}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                <div>
                  {renderFieldLabel("Start Date", "start_date")}
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className={`${FIELD_CLASS} w-full`}
                  />
                </div>
                <div>
                  {renderFieldLabel("End Date", "end_date")}
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className={`${FIELD_CLASS} w-full`}
                  />
                </div>
                <div>
                  {renderFieldLabel("Outlier Cap (hours)", "outlier_cap_hours")}
                  <select
                    value={outlierCap}
                    onChange={(e) => setOutlierCap(e.target.value)}
                    className={`${FIELD_CLASS} w-full`}
                  >
                    {CAP_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  {renderFieldLabel("Employee", "employee")}
                  <select
                    value={employeeId}
                    onChange={(e) => setEmployeeId(e.target.value)}
                    className={`${FIELD_CLASS} w-full`}
                  >
                    <option value="">All employees</option>
                    {staffOptions.map((staff) => (
                      <option key={staff.id} value={staff.id}>
                        {staff.display_name ||
                          staff.full_name ||
                          `Staff ${staff.id}`}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  {renderFieldLabel("Mix Time Bucket", "time_bucket")}
                  <select
                    value={timeBucket}
                    onChange={(e) => setTimeBucket(e.target.value)}
                    className={`${FIELD_CLASS} w-full`}
                  >
                    {TIME_BUCKET_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-2">
                  {renderInlineLabel("Duration Mode", "duration_mode")}
                  <button
                    type="button"
                    className={togglePillClass(
                      durationMode === "SINGLE_SERVICE_ONLY"
                    )}
                    onClick={() =>
                      setDurationMode((prev) =>
                        prev === "ALLOCATED"
                          ? "SINGLE_SERVICE_ONLY"
                          : "ALLOCATED"
                      )
                    }
                  >
                    Single-service tickets only
                  </button>
                </div>
                <div className="flex items-center gap-2">
                  {renderInlineLabel("Refunds", "include_refunds")}
                  <button
                    type="button"
                    className={togglePillClass(includeRefunds)}
                    onClick={() => setIncludeRefunds((prev) => !prev)}
                  >
                    Include refunds
                  </button>
                </div>
                <div className="flex items-center gap-2">
                  {renderInlineLabel("Discounts", "include_discounts")}
                  <button
                    type="button"
                    className={togglePillClass(includeDiscounts)}
                    onClick={() => setIncludeDiscounts((prev) => !prev)}
                  >
                    Include discounts
                  </button>
                </div>
                <div className="flex items-center gap-2">
                  {renderInlineLabel("Mix Share", "mix_share")}
                  <button
                    type="button"
                    className={togglePillClass(mixMetric === "net_sales")}
                    onClick={() => setMixMetric("net_sales")}
                  >
                    Net Sales
                  </button>
                  <button
                    type="button"
                    className={togglePillClass(mixMetric === "services_sold")}
                    onClick={() => setMixMetric("services_sold")}
                  >
                    Services Sold
                  </button>
                </div>
                <div className="ml-auto flex items-center gap-2">
                  <button
                    type="button"
                    onClick={resetFilters}
                    className={BTN_OUTLINE}
                  >
                    Clear
                  </button>
                  <button
                    type="button"
                    onClick={runReport}
                    className={BTN_PRIMARY}
                    disabled={loading}
                  >
                    {loading ? "Loading..." : "Run Report"}
                  </button>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              {TABS.map((tab) => {
                const tooltip = CATEGORY_ANALYTICS_TOOLTIPS[`tab_${tab.key}`];
                return (
                  <div key={tab.key} className="flex items-center gap-1.5">
                    <button
                      type="button"
                      className={`${TAB_BASE} ${
                        activeTab === tab.key ? TAB_ACTIVE : TAB_INACTIVE
                      }`}
                      onClick={() => setActiveTab(tab.key)}
                    >
                      {tab.label}
                    </button>
                    {tooltip ? (
                      <InfoTooltip text={tooltip} label={tab.label} />
                    ) : null}
                  </div>
                );
              })}
            </div>

            <div
              id="category-analytics-details"
              className={`space-y-6 ${getSectionClassName("category-analytics-details")}`}
            >
              <div
                id="category-analytics-export"
                className={`rounded-2xl border border-slate-200 bg-white p-4 text-sm text-slate-600 ${getSectionClassName(
                  "category-analytics-export"
                )}`}
              >
                Export is not available on this report.
              </div>
              {activeTab === "categoryTotals" && (
                <div className="space-y-6">
                  {!activeData ? (
                    emptyState
                  ) : (
                    <>
                      <div
                        id="category-analytics-kpis"
                        className={`grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 ${getSectionClassName(
                          "category-analytics-kpis"
                        )}`}
                      >
                        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                          <div className="text-xs uppercase text-slate-500">
                            Total Net Sales
                          </div>
                          <div className="text-2xl font-semibold text-slate-900">
                            {formatMoney(activeData?.totals?.net_sales)}
                          </div>
                        </div>
                        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                          <div className="text-xs uppercase text-slate-500">
                            Total Services Sold
                          </div>
                          <div className="text-2xl font-semibold text-slate-900">
                            {formatNumber(activeData?.totals?.services_sold, 0)}
                          </div>
                        </div>
                        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                          <div className="text-xs uppercase text-slate-500">
                            Categories
                          </div>
                          <div className="text-2xl font-semibold text-slate-900">
                            {formatNumber(activeData?.totals?.categories_count, 0)}
                          </div>
                        </div>
                      </div>

                      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
                        <div className="overflow-x-auto">
                          <table className="min-w-full text-sm report-table">
                            <thead className="bg-slate-50 text-slate-600">
                              <tr>
                                <SortableTh
                                  label="Category"
                                  sortKey="category"
                                  sortState={categoryTotalsSort.sortState}
                                  onSort={categoryTotalsSort.handleSort}
                                  tooltip={CATEGORY_ANALYTICS_TOOLTIPS.column_category}
                                  className="px-4 py-3 report-th-left"
                                  align="left"
                                />
                                <SortableTh
                                  label="Services Sold"
                                  sortKey="services_sold"
                                  sortState={categoryTotalsSort.sortState}
                                  onSort={categoryTotalsSort.handleSort}
                                  tooltip={CATEGORY_ANALYTICS_TOOLTIPS.column_services_sold}
                                  className="px-4 py-3 report-th-center"
                                  align="center"
                                />
                                <SortableTh
                                  label="Gross Sales"
                                  sortKey="gross_sales"
                                  sortState={categoryTotalsSort.sortState}
                                  onSort={categoryTotalsSort.handleSort}
                                  tooltip={CATEGORY_ANALYTICS_TOOLTIPS.column_gross_sales}
                                  className="px-4 py-3 report-th-center"
                                  align="center"
                                />
                                <SortableTh
                                  label="Discounts"
                                  sortKey="discounts"
                                  sortState={categoryTotalsSort.sortState}
                                  onSort={categoryTotalsSort.handleSort}
                                  tooltip={CATEGORY_ANALYTICS_TOOLTIPS.column_discounts}
                                  className="px-4 py-3 report-th-center"
                                  align="center"
                                />
                                <SortableTh
                                  label="Refunds"
                                  sortKey="refunds"
                                  sortState={categoryTotalsSort.sortState}
                                  onSort={categoryTotalsSort.handleSort}
                                  tooltip={CATEGORY_ANALYTICS_TOOLTIPS.column_refunds}
                                  className="px-4 py-3 report-th-center"
                                  align="center"
                                />
                                <SortableTh
                                  label="Net Sales"
                                  sortKey="net_sales"
                                  sortState={categoryTotalsSort.sortState}
                                  onSort={categoryTotalsSort.handleSort}
                                  tooltip={CATEGORY_ANALYTICS_TOOLTIPS.column_net_sales}
                                  className="px-4 py-3 report-th-center"
                                  align="center"
                                />
                                <SortableTh
                                  label="Share %"
                                  sortKey="share_percent"
                                  sortState={categoryTotalsSort.sortState}
                                  onSort={categoryTotalsSort.handleSort}
                                  tooltip={CATEGORY_ANALYTICS_TOOLTIPS.column_share_pct}
                                  className="px-4 py-3 report-th-center"
                                  align="center"
                                />
                                <SortableTh
                                  label="Avg Net / Service"
                                  sortKey="avg_net_per_service"
                                  sortState={categoryTotalsSort.sortState}
                                  onSort={categoryTotalsSort.handleSort}
                                  tooltip={CATEGORY_ANALYTICS_TOOLTIPS.column_avg_net_per_service}
                                  className="px-4 py-3 report-th-center"
                                  align="center"
                                />
                              </tr>
                            </thead>
                            <tbody>
                              {categoryTotalsSort.sortedRows.map((row) => (
                                <tr
                                  key={row.category_id ?? row.category_name}
                                  className="border-t border-slate-100"
                                >
                                  <td className="px-4 py-3 report-td-left text-slate-700">
                                    {row.category_name}
                                  </td>
                                  <td className="px-4 py-3 report-td-center text-slate-700">
                                    {formatNumber(row.services_sold, 0)}
                                  </td>
                                  <td className="px-4 py-3 report-td-center text-slate-700">
                                    {formatMoney(row.gross_sales)}
                                  </td>
                                  <td className="px-4 py-3 report-td-center text-slate-700">
                                    {formatMoney(row.discounts)}
                                  </td>
                                  <td className="px-4 py-3 report-td-center text-slate-700">
                                    {formatMoney(row.refunds)}
                                  </td>
                                  <td className="px-4 py-3 report-td-center text-slate-700">
                                    {formatMoney(row.net_sales)}
                                  </td>
                                  <td className="px-4 py-3 report-td-center text-slate-700">
                                    {formatPercent(row.share_percent)}
                                  </td>
                                  <td className="px-4 py-3 report-td-center text-slate-700">
                                    {row.avg_net_per_service != null
                                      ? formatMoney(row.avg_net_per_service)
                                      : "--"}
                                  </td>
                                </tr>
                              ))}
                              {categoryTotalsSort.sortedRows.length === 0 && (
                                <tr>
                                  <td
                                    className="px-4 py-4 text-sm text-slate-500"
                                    colSpan={8}
                                  >
                                    No categories found for this range.
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}

              {activeTab === "categoryMix" && (
                <div className="space-y-6">
                  {!activeData ? (
                    emptyState
                  ) : (
                    <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
                      <div className="overflow-x-auto">
                        <table className="min-w-full text-sm report-table">
                          <thead className="bg-slate-50 text-slate-600">
                            <tr>
                              <SortableTh
                                label="Bucket"
                                sortKey="bucket"
                                sortState={categoryMixSort.sortState}
                                onSort={categoryMixSort.handleSort}
                                tooltip={CATEGORY_ANALYTICS_TOOLTIPS.column_bucket}
                                className="px-4 py-3 report-th-left"
                                align="left"
                              />
                              <SortableTh
                                label="Total Net Sales"
                                sortKey="total_net_sales"
                                sortState={categoryMixSort.sortState}
                                onSort={categoryMixSort.handleSort}
                                tooltip={CATEGORY_ANALYTICS_TOOLTIPS.column_total_net_sales}
                                className="px-4 py-3 report-th-center"
                                align="center"
                              />
                              <SortableTh
                                label="Top Categories"
                                sortKey="top_categories"
                                sortState={categoryMixSort.sortState}
                                onSort={categoryMixSort.handleSort}
                                tooltip={CATEGORY_ANALYTICS_TOOLTIPS.column_top_categories}
                                className="px-4 py-3 report-th-left"
                                align="left"
                              />
                            </tr>
                          </thead>
                          <tbody>
                            {categoryMixSort.sortedRows.map((bucket) => (
                              <tr
                                key={bucket.bucket_start}
                                className="border-t border-slate-100"
                              >
                                <td className="px-4 py-3 report-td-left text-slate-700">
                                  {bucket.label}
                                </td>
                                <td className="px-4 py-3 report-td-center text-slate-700">
                                  {formatMoney(bucket.total_net_sales)}
                                </td>
                                <td className="px-4 py-3 report-td-left text-slate-700">
                                  <div className="space-y-1">
                                    {(bucket.categories || []).map((cat) => {
                                      const share =
                                        mixMetric === "services_sold"
                                          ? cat.share_services_sold
                                          : cat.share_net_sales;
                                      return (
                                        <div
                                          key={`${bucket.bucket_start}-${cat.category_name}`}
                                          className="text-xs text-slate-600"
                                        >
                                          <span className="font-semibold text-slate-700">
                                            {cat.category_name}
                                          </span>{" "}
                                          | {formatMoney(cat.net_sales)} |{" "}
                                          {formatPercent(share)}
                                        </div>
                                      );
                                    })}
                                    {(bucket.categories || []).length === 0 && (
                                      <div className="text-xs text-slate-500">
                                        No data.
                                      </div>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            ))}
                            {categoryMixSort.sortedRows.length === 0 && (
                              <tr>
                                <td
                                  className="px-4 py-4 text-sm text-slate-500"
                                  colSpan={3}
                                >
                                  No mix buckets found for this range.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}
              {activeTab === "categoryStaffing" && (
                <div className="space-y-6">
                  {!activeData ? (
                    emptyState
                  ) : (
                    <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
                      <div className="overflow-x-auto">
                        <table className="min-w-full text-sm report-table">
                          <thead className="bg-slate-50 text-slate-600">
                            <tr>
                              <SortableTh
                                label="Category"
                                sortKey="category"
                                sortState={categoryStaffingSort.sortState}
                                onSort={categoryStaffingSort.handleSort}
                                tooltip={CATEGORY_ANALYTICS_TOOLTIPS.column_category}
                                className="px-4 py-3 report-th-left"
                                align="left"
                              />
                              <SortableTh
                                label="Est Labor Hours"
                                sortKey="labor_hours"
                                sortState={categoryStaffingSort.sortState}
                                onSort={categoryStaffingSort.handleSort}
                                tooltip={CATEGORY_ANALYTICS_TOOLTIPS.column_est_labor_hours}
                                className="px-4 py-3 report-th-center"
                                align="center"
                              />
                              <SortableTh
                                label="Services Sold"
                                sortKey="services_sold"
                                sortState={categoryStaffingSort.sortState}
                                onSort={categoryStaffingSort.handleSort}
                                tooltip={CATEGORY_ANALYTICS_TOOLTIPS.column_services_sold}
                                className="px-4 py-3 report-th-center"
                                align="center"
                              />
                              <SortableTh
                                label="Net Sales"
                                sortKey="net_sales"
                                sortState={categoryStaffingSort.sortState}
                                onSort={categoryStaffingSort.handleSort}
                                tooltip={CATEGORY_ANALYTICS_TOOLTIPS.column_net_sales}
                                className="px-4 py-3 report-th-center"
                                align="center"
                              />
                              <SortableTh
                                label="Revenue / Labor Hour"
                                sortKey="revenue_per_labor_hour"
                                sortState={categoryStaffingSort.sortState}
                                onSort={categoryStaffingSort.handleSort}
                                tooltip={CATEGORY_ANALYTICS_TOOLTIPS.column_revenue_per_labor_hour}
                                className="px-4 py-3 report-th-center"
                                align="center"
                              />
                              <SortableTh
                                label="Top Employees"
                                sortKey="top_employees"
                                sortState={categoryStaffingSort.sortState}
                                onSort={categoryStaffingSort.handleSort}
                                tooltip={CATEGORY_ANALYTICS_TOOLTIPS.column_top_employees}
                                className="px-4 py-3 report-th-left"
                                align="left"
                              />
                            </tr>
                          </thead>
                          <tbody>
                            {categoryStaffingSort.sortedRows.map((row) => (
                              <tr
                                key={row.category_id ?? row.category_name}
                                className="border-t border-slate-100"
                              >
                                <td className="px-4 py-3 report-td-left text-slate-700">
                                  {row.category_name}
                                </td>
                                <td className="px-4 py-3 report-td-center text-slate-700">
                                  {row.labor_hours != null
                                    ? formatNumber(row.labor_hours, 1)
                                    : "--"}
                                </td>
                                <td className="px-4 py-3 report-td-center text-slate-700">
                                  {formatNumber(row.services_sold, 0)}
                                </td>
                                <td className="px-4 py-3 report-td-center text-slate-700">
                                  {formatMoney(row.net_sales)}
                                </td>
                                <td className="px-4 py-3 report-td-center text-slate-700">
                                  {row.revenue_per_labor_hour != null
                                    ? formatMoney(row.revenue_per_labor_hour)
                                    : "--"}
                                </td>
                                <td className="px-4 py-3 report-td-left text-slate-700">
                                  {renderTopEmployees(row.top_employees)}
                                </td>
                              </tr>
                            ))}
                            {categoryStaffingSort.sortedRows.length === 0 && (
                              <tr>
                                <td
                                  className="px-4 py-4 text-sm text-slate-500"
                                  colSpan={6}
                                >
                                  No staffing data found for this range.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {activeTab === "categoryInsights" && (
                <div className="space-y-6">
                  {!activeData ? (
                    emptyState
                  ) : (
                    <>
                      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
                        <div className="px-4 py-3 border-b border-slate-200">
                          <h3 className="text-lg font-semibold text-slate-900">
                            Growth vs Previous Period
                          </h3>
                        </div>
                        <div className="overflow-x-auto">
                          <table className="min-w-full text-sm report-table">
                            <thead className="bg-slate-50 text-slate-600">
                              <tr>
                                <SortableTh
                                  label="Category"
                                  sortKey="category"
                                  sortState={growthSort.sortState}
                                  onSort={growthSort.handleSort}
                                  tooltip={CATEGORY_ANALYTICS_TOOLTIPS.column_category}
                                  className="px-4 py-3 report-th-left"
                                  align="left"
                                />
                                <SortableTh
                                  label="Net Sales"
                                  sortKey="net_sales"
                                  sortState={growthSort.sortState}
                                  onSort={growthSort.handleSort}
                                  tooltip={CATEGORY_ANALYTICS_TOOLTIPS.column_net_sales}
                                  className="px-4 py-3 report-th-center"
                                  align="center"
                                />
                                <SortableTh
                                  label="Prev Net"
                                  sortKey="prev_net_sales"
                                  sortState={growthSort.sortState}
                                  onSort={growthSort.handleSort}
                                  tooltip={CATEGORY_ANALYTICS_TOOLTIPS.column_prev_net}
                                  className="px-4 py-3 report-th-center"
                                  align="center"
                                />
                                <SortableTh
                                  label="? Net"
                                  sortKey="delta_net_sales"
                                  sortState={growthSort.sortState}
                                  onSort={growthSort.handleSort}
                                  tooltip={CATEGORY_ANALYTICS_TOOLTIPS.column_change_net}
                                  className="px-4 py-3 report-th-center"
                                  align="center"
                                />
                                <SortableTh
                                  label="? %"
                                  sortKey="pct_change_net_sales"
                                  sortState={growthSort.sortState}
                                  onSort={growthSort.handleSort}
                                  tooltip={CATEGORY_ANALYTICS_TOOLTIPS.column_change_pct}
                                  className="px-4 py-3 report-th-center"
                                  align="center"
                                />
                                <SortableTh
                                  label="Services Sold"
                                  sortKey="services_sold"
                                  sortState={growthSort.sortState}
                                  onSort={growthSort.handleSort}
                                  tooltip={CATEGORY_ANALYTICS_TOOLTIPS.column_services_sold}
                                  className="px-4 py-3 report-th-center"
                                  align="center"
                                />
                              </tr>
                            </thead>
                            <tbody>
                              {growthSort.sortedRows.map((row) => (
                                <tr
                                  key={row.category_id ?? row.category_name}
                                  className="border-t border-slate-100"
                                >
                                  <td className="px-4 py-3 report-td-left text-slate-700">
                                    {row.category_name}
                                  </td>
                                  <td className="px-4 py-3 report-td-center text-slate-700">
                                    {formatMoney(row.net_sales)}
                                  </td>
                                  <td className="px-4 py-3 report-td-center text-slate-700">
                                    {formatMoney(row.prev_net_sales)}
                                  </td>
                                  <td className="px-4 py-3 report-td-center text-slate-700">
                                    {formatMoney(row.delta_net_sales)}
                                  </td>
                                  <td className="px-4 py-3 report-td-center text-slate-700">
                                    {row.pct_change_net_sales != null
                                      ? formatPercent(row.pct_change_net_sales)
                                      : "--"}
                                  </td>
                                  <td className="px-4 py-3 report-td-center text-slate-700">
                                    {formatNumber(row.services_sold, 0)}
                                  </td>
                                </tr>
                              ))}
                              {growthSort.sortedRows.length === 0 && (
                                <tr>
                                  <td
                                    className="px-4 py-4 text-sm text-slate-500"
                                    colSpan={6}
                                  >
                                    No growth data found for this range.
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                          <div className="text-xs uppercase text-slate-500">
                            Top 1 Share (Sales)
                          </div>
                          <div className="text-2xl font-semibold text-slate-900">
                            {formatPercent(
                              activeData?.concentration?.concentration
                                ?.top1_sales_share ??
                                activeData?.concentration?.top1_sales_share
                            )}
                          </div>
                        </div>
                        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                          <div className="text-xs uppercase text-slate-500">
                            Top 3 Share (Sales)
                          </div>
                          <div className="text-2xl font-semibold text-slate-900">
                            {formatPercent(
                              activeData?.concentration?.concentration
                                ?.top3_sales_share ??
                                activeData?.concentration?.top3_sales_share
                            )}
                          </div>
                        </div>
                        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                          <div className="text-xs uppercase text-slate-500">
                            Top 1 Share (Services)
                          </div>
                          <div className="text-2xl font-semibold text-slate-900">
                            {formatPercent(
                              activeData?.concentration?.concentration
                                ?.top1_services_share ??
                                activeData?.concentration?.top1_services_share
                            )}
                          </div>
                        </div>
                        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                          <div className="text-xs uppercase text-slate-500">
                            Top 3 Share (Services)
                          </div>
                          <div className="text-2xl font-semibold text-slate-900">
                            {formatPercent(
                              activeData?.concentration?.concentration
                                ?.top3_services_share ??
                                activeData?.concentration?.top3_services_share
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
                        <div className="px-4 py-3 border-b border-slate-200">
                          <h3 className="text-lg font-semibold text-slate-900">
                            Top Categories Concentration
                          </h3>
                        </div>
                        <div className="overflow-x-auto">
                          <table className="min-w-full text-sm report-table">
                            <thead className="bg-slate-50 text-slate-600">
                              <tr>
                                <SortableTh
                                  label="Category"
                                  sortKey="category"
                                  sortState={concentrationSort.sortState}
                                  onSort={concentrationSort.handleSort}
                                  tooltip={CATEGORY_ANALYTICS_TOOLTIPS.column_category}
                                  className="px-4 py-3 report-th-left"
                                  align="left"
                                />
                                <SortableTh
                                  label="Net Sales"
                                  sortKey="net_sales"
                                  sortState={concentrationSort.sortState}
                                  onSort={concentrationSort.handleSort}
                                  tooltip={CATEGORY_ANALYTICS_TOOLTIPS.column_net_sales}
                                  className="px-4 py-3 report-th-center"
                                  align="center"
                                />
                                <SortableTh
                                  label="Share %"
                                  sortKey="share_net_sales"
                                  sortState={concentrationSort.sortState}
                                  onSort={concentrationSort.handleSort}
                                  tooltip={CATEGORY_ANALYTICS_TOOLTIPS.column_share_pct}
                                  className="px-4 py-3 report-th-center"
                                  align="center"
                                />
                                <SortableTh
                                  label="Services Sold"
                                  sortKey="services_sold"
                                  sortState={concentrationSort.sortState}
                                  onSort={concentrationSort.handleSort}
                                  tooltip={CATEGORY_ANALYTICS_TOOLTIPS.column_services_sold}
                                  className="px-4 py-3 report-th-center"
                                  align="center"
                                />
                              </tr>
                            </thead>
                            <tbody>
                              {concentrationSort.sortedRows.map(
                                (row) => (
                                  <tr
                                    key={row.category_id ?? row.category_name}
                                    className="border-t border-slate-100"
                                  >
                                    <td className="px-4 py-3 report-td-left text-slate-700">
                                      {row.category_name}
                                    </td>
                                    <td className="px-4 py-3 report-td-center text-slate-700">
                                      {formatMoney(row.net_sales)}
                                    </td>
                                    <td className="px-4 py-3 report-td-center text-slate-700">
                                      {formatPercent(row.share_net_sales)}
                                    </td>
                                    <td className="px-4 py-3 report-td-center text-slate-700">
                                      {formatNumber(row.services_sold, 0)}
                                    </td>
                                  </tr>
                                )
                              )}
                              {concentrationSort.sortedRows.length === 0 && (
                                <tr>
                                  <td
                                    className="px-4 py-4 text-sm text-slate-500"
                                    colSpan={4}
                                  >
                                    No concentration data found for this range.
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
                        <div className="px-4 py-3 border-b border-slate-200">
                          <h3 className="text-lg font-semibold text-slate-900">
                            Seasonality (Last 12 Months)
                          </h3>
                        </div>
                        <div className="overflow-x-auto">
                          <table className="min-w-full text-sm report-table">
                            <thead className="bg-slate-50 text-slate-600">
                              <tr>
                                <SortableTh
                                  label="Month"
                                  sortKey="month"
                                  sortState={seasonalitySort.sortState}
                                  onSort={seasonalitySort.handleSort}
                                  tooltip={CATEGORY_ANALYTICS_TOOLTIPS.column_month}
                                  className="px-4 py-3 report-th-left"
                                  align="left"
                                />
                                <SortableTh
                                  label="Net Sales"
                                  sortKey="net_sales"
                                  sortState={seasonalitySort.sortState}
                                  onSort={seasonalitySort.handleSort}
                                  tooltip={CATEGORY_ANALYTICS_TOOLTIPS.column_net_sales}
                                  className="px-4 py-3 report-th-center"
                                  align="center"
                                />
                                <SortableTh
                                  label="Services Sold"
                                  sortKey="services_sold"
                                  sortState={seasonalitySort.sortState}
                                  onSort={seasonalitySort.handleSort}
                                  tooltip={CATEGORY_ANALYTICS_TOOLTIPS.column_services_sold}
                                  className="px-4 py-3 report-th-center"
                                  align="center"
                                />
                                <SortableTh
                                  label="Top Category"
                                  sortKey="top_category"
                                  sortState={seasonalitySort.sortState}
                                  onSort={seasonalitySort.handleSort}
                                  tooltip={CATEGORY_ANALYTICS_TOOLTIPS.column_top_category}
                                  className="px-4 py-3 report-th-left"
                                  align="left"
                                />
                                <SortableTh
                                  label="Top Categories"
                                  sortKey="top_categories"
                                  sortState={seasonalitySort.sortState}
                                  onSort={seasonalitySort.handleSort}
                                  tooltip={CATEGORY_ANALYTICS_TOOLTIPS.column_top_categories_compact}
                                  className="px-4 py-3 report-th-left"
                                  align="left"
                                />
                              </tr>
                            </thead>
                            <tbody>
                              {seasonalitySort.sortedRows.map(
                                (bucket) => {
                                  const topCategory = bucket.categories?.[0];
                                  return (
                                    <tr
                                      key={bucket.bucket_start}
                                      className="border-t border-slate-100"
                                    >
                                      <td className="px-4 py-3 report-td-left text-slate-700">
                                        {bucket.label}
                                      </td>
                                      <td className="px-4 py-3 report-td-center text-slate-700">
                                        {formatMoney(bucket.total_net_sales)}
                                      </td>
                                      <td className="px-4 py-3 report-td-center text-slate-700">
                                        {formatNumber(
                                          bucket.total_services_sold,
                                          0
                                        )}
                                      </td>
                                      <td className="px-4 py-3 report-td-left text-slate-700">
                                        {topCategory
                                          ? `${topCategory.category_name} ${formatPercent(
                                              topCategory.share_net_sales
                                            )}`
                                          : "--"}
                                      </td>
                                      <td className="px-4 py-3 report-td-left text-slate-700">
                                        {renderTopCategoriesCompact(bucket.categories)}
                                      </td>
                                    </tr>
                                  );
                                }
                              )}
                              {seasonalitySort.sortedRows.length === 0 && (
                                <tr>
                                  <td
                                    className="px-4 py-4 text-sm text-slate-500"
                                    colSpan={5}
                                  >
                                    No seasonality data found for this range.
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


